// Forward look: 72h FED projection, OT candidacy and 7-day rota risk.
// Port of the prototype's renderForward().

import React from 'react';
import { cn } from '@/lib/utils';
import { DEMO_MODE, GROUPS, REGIONS, WORKERS, readiness, wArr, workersInGroup, workersInRegion } from '../engine/data';
import type { Worker } from '../engine/data';
import { genInsight } from '../engine/insights';
import { lrmTone, rdyTone } from '../engine/scores';
import { AiInsight } from '../AiPanels';
import { scopedWorkers, useDash } from '../state';
import { Dot, Grid, Kpi, KpiRow, Panel, Pill, SecHeader, dataTable, fedTone, toneText } from '../ui';
import type { Tone } from '../ui';

const T = dataTable;

/** Colour a value by ascending thresholds (amber at `warn`, red at `bad`). */
export const thresholdTone = (v: number, warn: number, bad: number): Tone | undefined =>
  v >= bad ? 'bad' : v >= warn ? 'warn' : undefined;

export const signed = (n: number): string => (n > 0 ? `+${n}` : `${n}`);

const projected24 = (w: Worker): number => w.fed + (w.fed12[11] - w.fed12[8]);
const otEligible = (w: Worker): boolean => w.restHrs >= 11 && w.consec < 6 && 100 - w.fed > 30;

/** Region / group / individual pickers shared by the Forward and Shifts screens. */
export const ScopePickers: React.FC = () => {
  const d = useDash();

  if (d.drillScope === 'region') {
    return (
      <div className='flex flex-wrap gap-2'>
        {Object.entries(REGIONS).map(([rid, r]) => {
          const active = rid === d.activeRegion;
          return (
            <button
              key={rid}
              type='button'
              onClick={() => d.patch({ activeRegion: rid })}
              className='rounded-md border px-4 py-1.5 text-[13px] font-semibold transition-colors'
              style={
                active
                  ? {
                      borderColor: r.color,
                      color: r.color,
                      background: `color-mix(in srgb, ${r.color} 13%, transparent)`,
                    }
                  : undefined
              }
            >
              <span className={cn(!active && 'text-muted-foreground')}>{r.name}</span>
            </button>
          );
        })}
      </div>
    );
  }

  if (d.drillScope === 'group' && d.activeGroup !== null) {
    const grpRid = GROUPS[d.activeGroup].region;
    return (
      <div className='space-y-2'>
        <div className='flex flex-wrap gap-2'>
          {Object.entries(REGIONS).map(([rid, r]) => {
            const active = rid === grpRid;
            return (
              <button
                key={rid}
                type='button'
                onClick={() => d.patch({ activeRegion: rid, activeGroup: REGIONS[rid].groups[0] })}
                className='rounded-md border px-3 py-1 text-[11px] font-semibold transition-colors'
                style={
                  active
                    ? {
                        borderColor: r.color,
                        color: r.color,
                        background: `color-mix(in srgb, ${r.color} 13%, transparent)`,
                      }
                    : undefined
                }
              >
                <span className={cn(!active && 'text-muted-foreground')}>{r.name}</span>
              </button>
            );
          })}
        </div>
        <div className='flex flex-wrap gap-1.5'>
          {REGIONS[grpRid].groups.map((gid) => (
            <button
              key={gid}
              type='button'
              onClick={() => d.patch({ activeGroup: gid })}
              className={cn(
                'rounded-md border px-3.5 py-1.5 text-xs font-medium transition-colors',
                gid === d.activeGroup
                  ? 'border-success/30 bg-success/10 text-success'
                  : 'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {GROUPS[gid].name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (d.drillScope === 'individual') {
    const pool =
      d.activeGroup !== null
        ? workersInGroup(d.activeGroup)
        : d.activeRegion
          ? workersInRegion(d.activeRegion)
          : wArr();
    const sorted = [...pool].sort((a, b) => b.fed - a.fed);
    return (
      <div className='flex flex-wrap gap-1.5 border-b border-border pb-3'>
        <span className='mr-1 self-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
          Individual
        </span>
        {sorted.map((p) => (
          <button
            key={p.init}
            type='button'
            onClick={() => d.patch({ activeWorker: p.init })}
            className={cn(
              'flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
              p.init === d.activeWorker
                ? 'border-success/30 bg-success/10 text-success'
                : 'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            <Dot tone={rdyTone(readiness(p))} className='h-1.5 w-1.5' />
            {p.init} {p.name.split(' ')[0]}
          </button>
        ))}
      </div>
    );
  }

  return null;
};

/** Small coloured count tile used on the region cards. */
export const MiniStat: React.FC<{ value: React.ReactNode; label: string; tone?: Tone; alert?: boolean }> = ({
  value,
  label,
  tone,
  alert,
}) => (
  <div
    className={cn(
      'flex-1 rounded-lg border p-2 text-center',
      alert ? 'border-destructive/30 bg-destructive/10' : 'border-transparent bg-muted',
    )}
  >
    <div className={cn('text-xl font-bold tabular-nums', tone ? toneText[tone] : 'text-muted-foreground')}>{value}</div>
    <div className='text-[10px] text-muted-foreground'>{label}</div>
  </div>
);

export const RegionCardHeader: React.FC<{ color: string; name: string; count: number }> = ({ color, name, count }) => (
  <div className='mb-3 flex items-center gap-2'>
    <Dot color={color} className='h-2.5 w-2.5' />
    <span className='font-semibold'>{name}</span>
    <span className='ml-auto text-[11px] text-muted-foreground'>{count} workers</span>
  </div>
);

const days3 = ['Today', '+24h', '+48h'];

const ProjectionView: React.FC<{ workers: Worker[] }> = ({ workers }) => {
  const { drillWorker } = useDash();
  const otCands = workers.filter(otEligible).sort((a, b) => 100 - a.fed - (100 - b.fed));
  const critLrm = workers.filter((w) => w.lrm > 40).length;
  const at24 = workers.filter((w) => projected24(w) >= 80).length;
  const hiRisk = workers.filter((w) => w.consec >= 5 || w.fed >= 75 || w.lrm > 40).length;

  const projRows = [...workers].sort((a, b) => b.lrm - a.lrm || b.fed - a.fed);
  const rotaRows = [...workers].sort((a, b) => b.fed - a.fed);

  return (
    <div className='space-y-4'>
      <KpiRow>
        <Kpi label='Projected ≥80 FED in 24h' value={at24} tone={at24 ? 'bad' : 'good'} />
        {!DEMO_MODE.live && (
          <Kpi label='Critical LRM Now' value={critLrm} sub='Demand > readiness' tone={critLrm ? 'bad' : 'good'} />
        )}
        {!DEMO_MODE.live && (
          <Kpi label='OT Eligible (Tonight)' value={otCands.length} sub='≥11h rest, <6 consec, low LRM' tone='good' />
        )}
        <Kpi label='High-Risk Next 72h' value={hiRisk} tone='warn' />
      </KpiRow>

      <Grid cols={2}>
        <Panel
          title='72h FED Projection'
          sub={
            DEMO_MODE.live
              ? 'Extrapolated from 3-week trend'
              : 'Extrapolated from 3-week trend · LRM = current mismatch'
          }
        >
          <div className={T.wrap}>
            <table className={T.table}>
              <thead>
                <tr>
                  <th className={T.th}>Worker</th>
                  {days3.map((d) => (
                    <th key={d} className={cn(T.th, 'text-center')}>
                      {d}
                    </th>
                  ))}
                  {!DEMO_MODE.live && <th className={cn(T.th, T.num)}>LRM</th>}
                  <th className={T.th}>Risk</th>
                </tr>
              </thead>
              <tbody>
                {projRows.map((w) => {
                  const trend = (w.fed12[11] - w.fed12[8]) / 3;
                  const p1 = Math.min(Math.round(w.fed + trend), 100);
                  const p2 = Math.min(Math.round(w.fed + trend * 2), 100);
                  const crit = p2 >= 80 || w.lrm > 40;
                  const elev = p2 >= 65 || w.lrm > 20;
                  return (
                    <tr key={w.init} className={cn(T.tr, 'cursor-pointer')} onClick={() => drillWorker(w.init)}>
                      <td className={T.td}>
                        <strong>{w.name}</strong>
                      </td>
                      {[w.fed, p1, p2].map((v, i) => (
                        <td key={i} className={cn(T.td, 'text-center font-semibold', toneText[fedTone(v)])}>
                          {v}
                        </td>
                      ))}
                      {!DEMO_MODE.live && (
                        <td className={cn(T.td, T.num, 'font-semibold', toneText[lrmTone(w.lrm)])}>{signed(w.lrm)}</td>
                      )}
                      <td className={cn(T.td, 'text-center')}>
                        <Pill tone={crit ? 'bad' : elev ? 'warn' : 'good'}>
                          {crit ? 'Critical' : elev ? 'Elevated' : 'Low'}
                        </Pill>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title='OT Candidacy' sub='Eligible for additional shifts tonight'>
          {otCands.length ? (
            <div className={T.wrap}>
              <table className={T.table}>
                <thead>
                  <tr>
                    <th className={T.th}>#</th>
                    <th className={T.th}>Worker</th>
                    <th className={cn(T.th, T.num)}>FED</th>
                    <th className={cn(T.th, T.num)}>Rest</th>
                    <th className={cn(T.th, T.num)}>Consec</th>
                    <th className={T.th}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {otCands.map((w, i) => (
                    <tr key={w.init} className={cn(T.tr, 'cursor-pointer')} onClick={() => drillWorker(w.init)}>
                      <td className={cn(T.td, 'font-bold text-muted-foreground')}>{i + 1}</td>
                      <td className={T.td}>
                        <strong>{w.name}</strong>
                        <br />
                        <span className='text-[11px] text-muted-foreground'>{w.role}</span>
                      </td>
                      <td className={cn(T.td, T.num, toneText[fedTone(w.fed)])}>{w.fed}</td>
                      <td className={cn(T.td, T.num, w.restHrs >= 11 ? 'text-success' : 'text-destructive')}>
                        {w.restHrs}h
                      </td>
                      <td className={cn(T.td, T.num, w.consec >= 5 ? 'text-warning' : 'text-success')}>{w.consec}</td>
                      <td className={T.td}>
                        <Pill tone='good'>Eligible</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='rounded-lg bg-muted px-3 py-2.5 text-[13px] text-muted-foreground'>
              No workers currently eligible for OT.
            </div>
          )}
        </Panel>
      </Grid>

      <Panel title='Rota Risk Overview — Next 7 Days' sub='Workers at risk of exceeding FED 80 or WTR limits'>
        <div className={T.wrap}>
          <table className={T.table}>
            <thead>
              <tr>
                <th className={T.th}>Worker</th>
                <th className={T.th}>Group</th>
                <th className={cn(T.th, T.num)}>FED</th>
                <th className={cn(T.th, T.num)}>Consec</th>
                <th className={cn(T.th, T.num)}>Wk Hrs</th>
                <th className={cn(T.th, T.num)}>Drive km</th>
                <th className={T.th}>Outlook</th>
              </tr>
            </thead>
            <tbody>
              {rotaRows.map((w) => {
                const risk =
                  w.fed >= 80 || w.consec >= 6 || w.shiftHrs >= 52 ? 2 : w.fed >= 65 || w.consec >= 4 ? 1 : 0;
                const ct = thresholdTone(w.consec, 4, 6);
                const ht = thresholdTone(w.shiftHrs, 48, 55);
                const dt = thresholdTone(w.driveKm, 400, 700);
                return (
                  <tr key={w.init} className={cn(T.tr, 'cursor-pointer')} onClick={() => drillWorker(w.init)}>
                    <td className={T.td}>
                      <strong>{w.name}</strong>
                    </td>
                    <td className={cn(T.td, 'text-[11px] text-muted-foreground')}>{GROUPS[w.group].name}</td>
                    <td className={cn(T.td, T.num, 'font-semibold', toneText[fedTone(w.fed)])}>{w.fed}</td>
                    <td className={cn(T.td, T.num, ct && toneText[ct])}>{w.consec}</td>
                    <td className={cn(T.td, T.num, ht && toneText[ht])}>{w.shiftHrs}h</td>
                    <td className={cn(T.td, T.num, dt && toneText[dt])}>{w.driveKm}</td>
                    <td className={T.td}>
                      <Pill tone={(['good', 'warn', 'bad'] as const)[risk]}>{['Low', 'Moderate', 'High'][risk]}</Pill>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
};

const CompanyView: React.FC = () => {
  const d = useDash();
  const allW = wArr();
  const atRisk24 = allW.filter((w) => projected24(w) >= 80).length;
  const allOT = allW.filter(otEligible).length;
  const hiRisk = allW.filter((w) => w.consec >= 5 || w.fed >= 75).length;

  return (
    <div className='space-y-4'>
      <KpiRow>
        <Kpi label='Projected ≥80 FED in 24h' value={atRisk24} sub='Company-wide' tone={atRisk24 ? 'bad' : 'good'} />
        <Kpi label='OT Eligible' value={allOT} sub='≥11h rest, <6 consec' tone='good' />
        <Kpi label='High-Risk Next 72h' value={hiRisk} tone='warn' />
      </KpiRow>
      <AiInsight>{genInsight('forward', allW, { isCompany: true, scopeName: 'the company' })}</AiInsight>
      <div>
        <SecHeader>72h Risk by Region — click to view detail</SecHeader>
        <Grid cols={2}>
          {Object.entries(REGIONS).map(([rid, reg]) => {
            const rw = workersInRegion(rid);
            const crit = rw.filter((w) => projected24(w) >= 80).length;
            const ot = rw.filter(otEligible).length;
            const hi = rw.filter((w) => w.consec >= 5 || w.fed >= 75).length;
            return (
              <Panel key={rid} className='cursor-pointer transition-colors hover:bg-muted/40'>
                <div onClick={() => d.drillRegion(rid)}>
                  <RegionCardHeader color={reg.color} name={reg.name} count={rw.length} />
                  <div className='flex gap-2.5'>
                    <MiniStat value={crit} label='Critical 24h' tone={crit ? 'bad' : undefined} alert={crit > 0} />
                    <MiniStat value={ot} label='OT eligible' tone='good' />
                    <MiniStat value={hi} label='High-risk' tone={hi ? 'warn' : undefined} />
                  </div>
                </div>
              </Panel>
            );
          })}
        </Grid>
      </div>
    </div>
  );
};

export const ForwardScreen: React.FC = () => {
  const d = useDash();
  const workers = scopedWorkers(d);

  if (d.drillScope === 'company') return <CompanyView />;

  let scopeName = '';
  let regionId: string | undefined;
  if (d.drillScope === 'region' && d.activeRegion) {
    scopeName = REGIONS[d.activeRegion].name;
    regionId = d.activeRegion;
  } else if (d.drillScope === 'group' && d.activeGroup !== null) {
    scopeName = GROUPS[d.activeGroup].name;
  } else if (d.activeWorker) {
    scopeName = WORKERS[d.activeWorker].name;
  }

  return (
    <div className='space-y-4'>
      <ScopePickers />
      <AiInsight>{genInsight('forward', workers, { regionId, scopeName })}</AiInsight>
      <ProjectionView workers={workers} />
    </div>
  );
};
